import { Alert, Button, Loader, Stack, Text } from '@mantine/core'
import { IconCheck, IconExclamationMark } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'

interface AccountConfirmationStepProps {
  customerInfoUuid: string
  onBack?: () => void
  onComplete?: () => void
}

export function AccountConfirmationStep({ customerInfoUuid, onBack, onComplete }: AccountConfirmationStepProps) {
  const { api } = useGlobalState()
  const [customerCreated, setCustomerCreated] = useState(false)

  const createCustomerMutation = useMutation({
    mutationFn: async () => {
      return await api.createCustomer({
        customer_info_uuid: customerInfoUuid,
      })
    },
    onSuccess: _response => {
      setCustomerCreated(true)
      onComplete?.()
    },
    onError: _error => {
      // Error handling is managed through the mutation state
    },
  })

  // Automatically start customer creation when component mounts
  useEffect(() => {
    if (!customerCreated && !createCustomerMutation.isPending && !createCustomerMutation.error) {
      createCustomerMutation.mutate()
    }
  }, [customerCreated, createCustomerMutation])

  // Show loading state while creating customer
  if (createCustomerMutation.isPending) {
    return (
      <Stack gap="lg" mt="xl" align="center">
        <Loader size="lg" />
        <Stack gap="xs" align="center">
          <Text size="lg" fw={500} ta="center">
            Setting Up Your Account
          </Text>
          <Text size="md" ta="center" c="dimmed">
            We&apos;re creating your account, this will only take a moment...
          </Text>
        </Stack>
      </Stack>
    )
  }

  // Show error state if customer creation failed
  if (createCustomerMutation.error) {
    return (
      <Stack gap="md" mt="xl" align="center">
        <Alert variant="light" color="red" title="Account Setup Error" icon={<IconExclamationMark />}>
          There was an issue setting up your account.
        </Alert>

        <Stack gap="md" align="center" mt="lg">
          <Button size="lg" onClick={() => createCustomerMutation.mutate()}>
            Try Again
          </Button>
          {onBack && (
            <Button size="md" variant="default" onClick={onBack}>
              Go Back
            </Button>
          )}
        </Stack>

        <Text size="md" ta="center" mt="lg">
          If the problem persists, please contact our support team at{' '}
          <a href="mailto:support@sidekickwellness.com" style={{ color: '#228be6', textDecoration: 'underline' }}>
            support@sidekickwellness.com
          </a>
          .
        </Text>
      </Stack>
    )
  }

  // Show final success state when customer is created
  if (customerCreated) {
    return (
      <Stack gap="lg" mt="xl" align="center">
        <IconCheck size={64} color="#51cf66" />

        <Stack gap="xs" align="center">
          <Text size="xl" fw={600} ta="center">
            Sign Up Complete!
          </Text>
          <Text size="xl" fw={600} ta="center">
            Welcome to Sidekick Platform.
          </Text>
        </Stack>

        <Stack gap={0} align="center" mt="lg" mb="xl">
          <Text size="md" ta="center">
            Please check your email for instructions on setting up your account.
          </Text>
        </Stack>

        <Text size="sm" ta="center" c="dimmed" mt="md">
          If you don&apos;t receive an email within 24 hours, please contact{' '}
          <a href="mailto:support@sidekickwellness.com" style={{ color: '#228be6', textDecoration: 'underline' }}>
            support@sidekickwellness.com
          </a>
          .
        </Text>
      </Stack>
    )
  }
}
